import { useAddToTrash } from "@/api/trash";
import MediaLibrary, { ImageFormData } from "@/components/images/MediaLibrary";
import ConfirmNavigation from "@/components/navigation/ConfirmNavigation";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
} from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/hooks/use-toast";
import { useFormAutosave } from "@/hooks/useAutosave";
import { TypeOptionPosition } from "@/lib/image";
import { capitalizeFirstLetter, WithPartial } from "@/lib/utils";
import { ChevronRightIcon } from "lucide-react";
import { ReactNode, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";
import FormHeader, { FormHeaderProps } from "./FormHeader";
import {
  FormHeadingMetadata,
  FormHeadingMetadataProps,
} from "./FormHeadingMetadata";
import { useAuth } from "@/context/auth/AuthContext";

export type FormLayoutProps<T extends FieldValues> = Omit<
  WithPartial<FormHeaderProps, "onAddToTrash">,
  "backLabel"
> &
  Omit<FormHeadingMetadataProps<T>, "onClick"> & {
    type: TypeOptionPosition;
    id: string;
    children?: ReactNode;
    invalidateOnChange?: () => void;
    computeImageName?: (data?: ImageFormData) => string;
    embedAttachmentOnSubmit?: boolean;
  };

export const FormLayout = <T extends FieldValues>({
  children,
  onSave,
  linkOverview,
  reference,
  title,
  subtitle,
  picturesData,
  id,
  type,
  onAddToTrash,
  publicationStatus,
  form,
  print,
  relativePosition,
  invalidateOnChange,
  computeImageName,
  embedAttachmentOnSubmit = false,
  keyStatus,
}: FormLayoutProps<T> & { invalidateOnchange?: () => void }) => {
  const [imageMode, setImageMode] = useState<boolean>(false);
  const trashAdder = useAddToTrash();
  const { user } = useAuth();
  const isReader = user?.role === "reader" ? true : false;

  const { t } = useTranslation();

  useFormAutosave(form, () => onSave(), isReader);

  const handleAddToTrash = () => {
    if (publicationStatus === "bin") {
      toast({
        title: t("documentAlreadyInTrashToastTitle"),
        variant: "success",
        description: t("documentAlreadyInTrashToastDescription"),
      });
    } else {
      trashAdder.mutate(
        { documentId: id, documentType: type },
        {
          onSuccess: () => {
            toast({
              title: t("documentAddedToTrashToastTitle"),
              variant: "success",
              description: t("documentAddedToTrashToastDescription"),
            });
            form.reset(undefined, { keepValues: true });
            onAddToTrash?.();
          },
        },
      );
    }
  };

  return (
    <>
      <FormHeader
        onAddToTrash={handleAddToTrash}
        addToTrashDisabled={publicationStatus === "bin" || trashAdder.isPending}
        title={title}
        backLabel={t(`retourDashboardDepuis${capitalizeFirstLetter(type)}`)}
        onSave={onSave}
        linkOverview={linkOverview}
        print={print}
        relativePosition={relativePosition}
      />
      {imageMode ? (
        <div className="max-w-screen-2xl mx-auto px-8 mt-[70px]">
          <Breadcrumb className="text-left flex items-center gap-1 mb-4">
            <BreadcrumbItem>
              <BreadcrumbLink
                onClick={() => setImageMode(false)}
                className="text-gray-600 text-sm cursor-pointer"
              >
                {title ?? reference}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <ChevronRightIcon className="w-3 h-3 text-gray-300" />
            <BreadcrumbItem>
              <p className="text-primary font-[600] text-sm">
                {t("thumbnailManageImages_action")}
              </p>
            </BreadcrumbItem>
          </Breadcrumb>
          {type && (
            <MediaLibrary
              computeImageName={computeImageName}
              id={id}
              invalidateOnChange={invalidateOnChange}
              type={type}
              embedAttachmentOnSubmit={embedAttachmentOnSubmit}
              setImageMode={setImageMode}
            />
          )}
        </div>
      ) : (
        <>
          <FormHeadingMetadata
            reference={reference}
            title={title}
            form={form}
            subtitle={subtitle}
            picturesData={picturesData}
            publicationStatus={publicationStatus}
            onClick={() => setImageMode(true)}
            keyStatus={keyStatus}
          />
          <Separator />
          <ConfirmNavigation
            isBlocked={
              form.formState.isDirty &&
              Object.keys(form.formState.dirtyFields).length > 0
            }
          />
          {children}
        </>
      )}
      :
    </>
  );
};
